/**
 * CL-397B — Saved search workspace copy, routes, and helpers.
 * CL-397C — Search-owned route at /search/saved (not legacy Property Intelligence Tools).
 */

export const SAVED_WORKSPACE_TITLE = "Saved properties & compare";
export const SAVED_WORKSPACE_EYEBROW = "Search workspace";
/** Search subnav pill label. */
export const SAVED_WORKSPACE_SUBNAV_LABEL = "Saved properties";
export const SAVED_WORKSPACE_CTA_LABEL = "Open saved properties";
export const SAVED_WORKSPACE_LEGACY_ROUTE = "/client-portal/real-estate/saved";
export const SAVED_WORKSPACE_LEGACY_HUB_TAB = "saved";

/** Canonical Search-owned saved workspace route. */
export const SAVED_WORKSPACE_ROUTE = "/client-portal/real-estate/search/saved";

export function buildSavedWorkspacePath(extraSearch = "") {
  const params = new URLSearchParams(extraSearch.startsWith("?") ? extraSearch.slice(1) : extraSearch);
  params.delete("tab");
  const q = params.toString();
  return q ? `${SAVED_WORKSPACE_ROUTE}?${q}` : SAVED_WORKSPACE_ROUTE;
}

export const SAVED_WORKSPACE_SEARCH_PATHS = {
  condos: "/client-portal/real-estate/condos",
  neighbourhoods: "/client-portal/real-estate/search/neighbourhoods",
  myProperties: "/client-portal/real-estate/properties",
  houses: "/client-portal/real-estate/search/houses",
};

export const SAVED_WORKSPACE_INTRO =
  "Candidate listings and search shortlists — not your owned property files. Star promising condos today; houses, neighbourhoods, and MLS comparables will land here as search tools expand.";

export const SAVED_WORKSPACE_EMBEDDED_INTRO =
  "Your search working set: shortlist, compare tray, and named collections. Saves flow here from condo search today — and from future house listings, neighbourhood bookmarks, and MLS sold comps. My Properties is for owned addresses with documents, Intelligence, and CMA.";

export const SAVED_WORKSPACE_EMPTY_STATE =
  "Saved listings, candidate homes, compare trays, and named collections live here — fed by Search lanes, not My Properties. Start in condo search or browse neighbourhoods, then return between visits.";

export const SHORTLIST_SECTION_TITLE = "Shortlist";
export const SHORTLIST_SECTION_HELP =
  "Saved candidates from condo search, future house listings, neighbourhood research, and MLS tools — starred or added as search lanes expand.";
export const SHORTLIST_EMPTY_HELP =
  "Star a condo building or unit, or save candidates from house search and neighbourhood research as those lanes connect.";

export const COMPARE_TRAY_SECTION_TITLE = "Compare tray";
export const COMPARE_TRAY_SECTION_HELP =
  "Temporary side-by-side comparison (up to four slots) for discussion — snapshots only, not a valuation.";
export const COMPARE_TRAY_EMPTY_HELP = "Add units from condo search to compare fees, size, and notes side by side.";

export const COLLECTIONS_SECTION_TITLE = "Collections";
export const COLLECTIONS_SECTION_HELP =
  "Named groups of saved candidates — useful when you want to share a shortlist with Mark before showings or offers.";

export const BROWSE_SUGGESTIONS_TITLE = "Browse suggestions";
export const BROWSE_SUGGESTIONS_HELP =
  "Early condo district suggestions from how you browse — context only, not personalized MLS picks. More property types coming.";
export const BROWSE_SUGGESTIONS_EMPTY =
  "Save a candidate or browse a few districts in condo search — early suggestions may appear here.";

export const STALE_UPDATES_TITLE = "Updates on saved candidates";
export const STALE_UPDATES_HELP =
  "on your shortlist or compare tray may have new research edits since you last acknowledged.";

export const COMPARE_LIMIT_ERROR =
  "Compare is limited to four candidates. Remove one in your saved search workspace.";

/** Primary workspace CTAs — each routes to a live surface. */
export const SAVED_WORKSPACE_CTAS = [
  {
    key: "condos",
    label: "Open condo search",
    to: SAVED_WORKSPACE_SEARCH_PATHS.condos,
    variant: "primary",
  },
  {
    key: "neighbourhoods",
    label: "Browse neighbourhoods",
    to: SAVED_WORKSPACE_SEARCH_PATHS.neighbourhoods,
    variant: "secondary",
  },
  {
    key: "my_properties",
    label: "Go to My Properties",
    to: SAVED_WORKSPACE_SEARCH_PATHS.myProperties,
    variant: "secondary",
  },
  {
    key: "mls_comps",
    label: "MLS sold comps — coming next",
    to: SAVED_WORKSPACE_SEARCH_PATHS.myProperties,
    variant: "future",
    title: "MLS sold comparables and CMA candidates will save here after MLS integration.",
  },
];

/**
 * @param {{ shortlist?: unknown[], compare?: unknown[], collections?: unknown[] }} state
 */
export function isSavedWorkspaceEmpty(state) {
  const shortlist = state?.shortlist ?? [];
  const compare = state?.compare ?? [];
  const collections = state?.collections ?? [];
  return shortlist.length === 0 && compare.length === 0 && collections.length === 0;
}
